import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ClockIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useAuthBusiness, getNormalizedSector } from '../../contexts/AuthBusinessContext';
import { useAccessControl } from '../../hooks/useAccessControl';
import { useModuleAccess } from '../../contexts/ModuleAccessContext';
import MenuConfigService from '../../services/menuConfigService';
import MenuModuleService from '../../services/MenuModuleService';
import sectoralSidebarService from '../../services/sectoralSidebarService';
import { businessUsersService } from '../../services/businessUsersService';
import { getWorkingRoute } from '../../utils/routeMapping';
import LocalAgentStatusBadge from '../common/LocalAgentStatusBadge';
import {
  POSIcon,
  DashboardIcon,
  OrderIcon,
  TableIcon,
  MenuIcon,
  CustomerIcon,
  ReportIcon,
  PaymentIcon,
  NotificationIcon,
  IntegrationIcon,
  SettingsIcon
} from './PosIcons';
import {
  ComputerDesktopIcon,
  PhoneIcon,
  UserIcon
} from '@heroicons/react/24/outline';

// Helper function for sector display names
const getSectorDisplayName = (sectorType) => {
  const sectorNames = {
    'food-beverage': 'Yiyecek & İçecek',
    'food_beverage': 'Yiyecek & İçecek',
    'sectoral_food-beverage': 'Yiyecek & İçecek',
    'sectoral_food_beverage': 'Yiyecek & İçecek',
    food: 'Yiyecek & İçecek',
    bakery: 'Fırın/Pastane',
    restaurant: 'Restoran',  // Legacy compatibility
    retail: 'Perakende',
    service: 'Hizmet',
    hospitality: 'Otelcilik'
  };
  return sectorNames[sectorType] || 'Bilinmeyen Sektör';
};

// Helper for business type label
const getBusinessTypeLabel = (business) => {
  if (!business) return 'İşletme';
  if (business.id === 'business_account' || business.type === 'business_account' || business.business_type === 'management') {
    return 'Hesap Yönetimi';
  }

  const normSector = getNormalizedSector(business);
  switch (normSector) {
    case 'hospitality': return 'Otelcilik';
    case 'food-beverage': return 'Yeme-İçme';
    case 'retail': return 'Perakende';
    case 'service': return 'Hizmet';
    default: return 'İşletme';
  }
};

// Normalize sector names to standard format using central logic
const normalizeSectorForSidebar = (sectorValue, business, sidebarType) => {
  // If we already have a sectoral sidebar type from context, map it to our internal sector keys
  if (sidebarType === 'sectoral_hospitality') return 'hospitality';
  if (sidebarType === 'sectoral_retail') return 'retail';
  if (sidebarType === 'sectoral_service') return 'service';
  if (sidebarType === 'sectoral_food-beverage') return 'food-beverage';

  // Use the central normalization logic as fallback or validation
  return getNormalizedSector(business || { sector: sectorValue });
};

const BusinessSidebar = ({ isCollapsed, onToggle, onClose, sectorType = 'food' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness, currentUserRole, logout, toggleSidebar, sidebarType, isLoading: authLoading } = useAuthBusiness();
  const { hasSubscription, activeModuleIds, packageName: moduleAccessPackageName, loading: moduleLoading } = useModuleAccess();
  // ModuleAccessContext'ten gelen packageName'i de bir değişkene al
  const moduleAccess = { packageName: moduleAccessPackageName };

  // Normalize sector based on selected business or prop
  const currentSector = normalizeSectorForSidebar(
    selectedBusiness?.sector || sectorType, 
    selectedBusiness,
    sidebarType
  );

  // State
  const [currentTime, setCurrentTime] = useState(new Date());
  const [menuConfig, setMenuConfig] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);

  // Get user and business info
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isAuthenticated } = useAuthBusiness();

  // Initial load and dynamic menu generation
  useEffect(() => {
    let isMounted = true;

    const loadSidebarConfig = async () => {
      // Use business from context primarily, but fallback to local for initial hydration if needed
      const business = selectedBusiness || JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      console.log('🏢 BusinessSidebar: Active business:', business);

      // Generate menu based on active modules and sector
      const currentSector = normalizeSectorForSidebar(business?.sector, business, sidebarType);
      
      try {
        console.log(`🌐 BusinessSidebar: Loading menu for sector: ${currentSector}`);
        
        // 1. Get safe modules list
        const safeModules = (activeModuleIds && activeModuleIds.length > 0) ? activeModuleIds : ['core_access'];
        
        let visibleItems = [];

        // 2. Load sectoral sidebar items (All sectors now use this unified system)
        const sectoralMenu = await sectoralSidebarService.getSectoralSidebar(currentSector);
        if (!isMounted) return;

        const filterBusiness = { 
          ...selectedBusiness, 
          modules: safeModules,
          add_ons: selectedBusiness?.add_ons || selectedBusiness?.subscription?.add_ons || [],
          // Full paket bypass için subscription ve packageName bilgisi
          subscription: selectedBusiness?.subscription || {},
          packageName: moduleAccess?.packageName || selectedBusiness?.subscription?.package_name || ''
        };

        visibleItems = sectoralSidebarService.filterVisibleItems(sectoralMenu, filterBusiness);

        // 3. 🛡️ SaaS Standart Rol ve Yetki Filtrelemesi (Kasiyer, Garson, Mutfak, Kurye vb.)
        const activeRole = (selectedBusiness?.role || currentUserRole || user?.role || user?.user_type || 'employee').toLowerCase();
        const isOwnerRole = activeRole === 'owner' || activeRole === 'business_owner' || activeRole === 'business_accounts' || activeRole === 'branch_manager' || activeRole === 'system_admin' || activeRole === 'super_admin' || Boolean(user?.is_business_account) || Boolean(user?.is_branch_account);

        // Menü ID -> Yetki Anahtarı Eşleme
        const MENU_PERMISSION_MAP = {
          'restaurant_dashboard': 'dashboard_access',
          'pos': 'pos_access',
          'order-tracking': 'order_management',
          'operations': 'kitchen_access',
          'campaigns': 'customer_management',
          'whatsapp': 'customer_management',
          'finance': 'financial_reports',
          'reports': 'reports_view',
          'hr_management': 'hr_management',
          'settings': 'settings_basic'
        };

        if (!isOwnerRole) {
          const userPermissions = businessUsersService.getUserPermissions(activeRole);
          const hasAnyStar = userPermissions.includes('*');

          if (!hasAnyStar) {
            visibleItems = visibleItems.filter(item => {
              const reqPerm = MENU_PERMISSION_MAP[item.id];
              if (!reqPerm) return true; // Özel izin tanımlı değilse göster
              return userPermissions.includes(reqPerm);
            });
          }
        }

        // 3.1 Special Role Check (Cari)
        const isCari = activeRole === 'cari';
        if (isCari) {
          visibleItems = visibleItems.map(item => {
            if (item.type === 'single') {
              const isDistribution = item.path?.includes('operations/distribution') || item.moduleId === 'stock_basic';
              return isDistribution ? item : null;
            }
            if (item.type === 'group') {
              const filteredChildren = item.children?.filter(child =>
                child.path?.includes('operations/distribution') || child.moduleId === 'stock_basic'
              );
              return filteredChildren?.length > 0 ? { ...item, children: filteredChildren } : null;
            }
            return null;
          }).filter(Boolean);
        }

        // 4. Validate routes (mapping to working routes)
        const validatedItems = MenuConfigService.validateMenuRoutes(visibleItems);

        console.log('✅ BusinessSidebar: Final rendered items:', validatedItems.length);
        if (isMounted) {
          setMenuConfig(validatedItems);
          setLoading(false);
          console.log(`✅ BusinessSidebar: Menu loaded for ${currentSector}. Visible items: ${validatedItems.length}`);
        }
      } catch (error) {
        console.error('❌ BusinessSidebar: Error loading menu:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // 🛡️ WAIT FOR IDENTITY: Don't load menu until we are sure who we are
    if (!moduleLoading && !authLoading && selectedBusiness?.id) {
       loadSidebarConfig();
    } else if (selectedBusiness?.id && !authLoading) {
       // We have business but modules still loading
       loadSidebarConfig();
    }

    // Update time every minute
    const timer = setInterval(() => {
      if (isMounted) setCurrentTime(new Date());
    }, 60000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [activeModuleIds, sectorType, moduleLoading, isAuthenticated, authLoading, selectedBusiness?.id]); // Re-run when modules, sector, auth or loading state changes

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getBusinessIcon = (businessType) => {
    switch (businessType) {
      case 'restaurant':
        return '🍽️';
      case 'retail':
        return '🏪';
      case 'service':
        return '💄';
      default:
        return '🏢';
    }
  };

  const isRouteActive = React.useCallback((itemPath) => {
    if (!itemPath) return false;
    const current = location.pathname;
    const targetRoute = getWorkingRoute(itemPath);
    if (!targetRoute) return false;
    
    if (current === targetRoute) return true;

    if (targetRoute.length > 5 && targetRoute !== '/dashboard' && targetRoute !== '/sectors/food-beverage/dashboard') {
      if (current.startsWith(targetRoute + '/')) return true;
      const baseCategory = targetRoute.replace(/\/dashboard$/, '');
      if (baseCategory.length > 8 && current.startsWith(baseCategory)) return true;
    }

    return false;
  }, [location.pathname]);

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-2xl transition-all duration-300 flex flex-col w-full h-full relative z-10">

        {/* Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center p-3' : 'justify-between p-4'} border-b border-gray-700`}>
          {!isCollapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
                {(selectedBusiness?.logo || selectedBusiness?.logo_url || selectedBusiness?.logoUrl) ? (
                  <img
                    src={selectedBusiness.logo || selectedBusiness.logo_url || selectedBusiness.logoUrl}
                    alt={selectedBusiness.name}
                    className="w-full h-full object-cover bg-white"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-2xl">
                    {(selectedBusiness?.name || 'İ').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-white font-semibold text-lg truncate" title={selectedBusiness?.name}>
                  {selectedBusiness?.name && selectedBusiness.name !== 'İşletme' ? selectedBusiness.name : 'İşletme'}
                </h2>
                <p className="text-gray-400 text-xs mt-0.5">
                  {getSectorDisplayName(currentSector)} • Aktif
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            {/* Desktop Collapse Button */}
            <button
              onClick={onToggle}
              className="hidden lg:flex p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="w-5 h-5" />
              ) : (
                <ChevronLeftIcon className="w-5 h-5" />
              )}
            </button>

            {/* Mobile Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors lg:hidden"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Time and Date */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <ClockIcon className="w-4 h-4" />
                <span>{formatTime(currentTime)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <CalendarDaysIcon className="w-4 h-4" />
                <span>{formatDate(currentTime)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-650 scrollbar-track-transparent hover:scrollbar-thumb-gray-500">
          {loading ? (
             <div className="flex flex-col items-center justify-center h-40 space-y-3">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-500 text-sm">Yükleniyor...</span>
             </div>
          ) : (
            <nav className="space-y-2 px-3 pb-16">
              {menuConfig.length === 0 ? (
                 <div className="px-4 py-8 text-center">
                    <p className="text-gray-500 text-sm italic">Erişilebilir modül bulunamadı.</p>
                 </div>
              ) : menuConfig.map((item, index) => {
                const itemActive = isRouteActive(item.path);
                if (item.type === 'single') {
                  return (
                    <Link
                      key={`single-${item.id || item.path}-${index}`}
                      to={getWorkingRoute(item.path)}
                      onClick={() => onClose && onClose()}
                      className={`flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200 group ${itemActive
                        ? 'bg-gradient-to-r ' + (item.color || 'from-gray-700 to-gray-600') + ' text-white shadow-lg font-bold'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                        }`}
                    >
                      <div className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : ''}`}>
                        {MenuConfigService.renderIcon(item.icon, 'w-5 h-5')}
                      </div>
                      {!isCollapsed && (
                        <span className="font-medium text-sm">{item.label}</span>
                      )}
                    </Link>
                  );
                }

                if (item.type === 'group') {
                  const hasActiveChild = item.children?.some(c => isRouteActive(c.path));
                  const isExpanded = expandedGroups[item.id] || hasActiveChild;
                  return (
                    <div key={`group-${item.id}-${index}`}>
                      <button
                        onClick={() => toggleGroup(item.id)}
                        className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 group ${hasActiveChild ? 'text-white bg-gray-800/80 font-bold' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : ''}`}>
                            {MenuConfigService.renderIcon(item.icon, 'w-5 h-5')}
                          </div>
                          {!isCollapsed && (
                            <span className="font-medium text-sm">{item.label}</span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <ChevronRightIcon
                            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                          />
                        )}
                      </button>

                      {!isCollapsed && isExpanded && (
                        <div className="mt-1 ml-8 space-y-1">
                          {item.children && item.children.map((child, childIndex) => {
                            const childActive = isRouteActive(child.path);
                            return (
                              <Link
                                key={`child-${item.id}-${child.path || child.id}-${childIndex}`}
                                to={getWorkingRoute(child.path)}
                                onClick={() => onClose && onClose()}
                                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${childActive
                                  ? 'bg-indigo-600 text-white font-bold shadow-md'
                                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                  }`}
                              >
                                {child.icon && MenuConfigService.renderIcon(child.icon, 'w-4 h-4')}
                                <span>{child.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </nav>
          )}
        </div>

        {/* 🌐 Online / Offline Kasa & Bulut Durumu (Sidebar Footer) */}
        <div className="border-t border-gray-800/80 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center shrink-0">
          <LocalAgentStatusBadge variant="sidebar" isCollapsed={isCollapsed} />
        </div>

      </div>
  );
};

export default BusinessSidebar;